/* eslint-disable @typescript-eslint/no-explicit-any */

import { IconInfoCircle } from '@tabler/icons-react'
import VisualizationsBasicMap from '../visualization-charts/VisualizationsBasicMap'
import VisualizationsBasicBar from '../visualization-charts/VisualizationsBasicBar'
import VisualizationsBasicColumn from '../visualization-charts/VisualizationsBasicColumn'
import { useEffect, useState } from 'react'
import { fetchThreatIntelApt, selectAdversaries } from '../../../reducers/adversaries'
import { useAppDispatch } from '../../../reducers/store'
import { useSelector } from 'react-redux'
import { AttacksHeatmapMapperData, BarChart } from '../../../types/StatTypes'
import {
  mapIntelToAptSourceMap,
  mapIntelToAptTargetMap,
  mapIntelToThreatsActorEventsIn12Months,
  mapIntelToThreatsByActorSource,
  mapIntelToTopThreatActorsIn12Months,
} from '../../../utils/malware-mapper'
import PageSkeleton from '../../skeleton/PageSkeleton'
import VisualizationsBasicHeatmap from '../visualization-charts/VisualizationsBasicHeatmap'
import { Tooltip } from '@radix-ui/themes'

interface IVisualizationAptPageProps {
  mapData: object
}

function VisualizationAptPage(props: IVisualizationAptPageProps) {
  const { mapData } = props
  const di = useAppDispatch()
  const { aptData } = useSelector(selectAdversaries)

  useEffect(() => {
    di(fetchThreatIntelApt())
  }, [])

  const [intelForTopThreatActorsIn12Months, setIntelForTopThreatActorsIn12Months] = useState<BarChart>([])
  const [intelForThreatsActorEventsIn12Months, setIntelForThreatsActorEventsIn12Months] =
    useState<AttacksHeatmapMapperData>({
      yAxis: [],
      xAxis: [],
      heatmapData: [],
    })
  const [intelForThreatsByActorSourceCategories, setIntelForThreatsByActorSourceCategories] = useState<string[]>([])
  const [intelForThreatsByActorSourceSeries, setIntelForThreatsByActorSourceSeries] = useState<any[]>([])
  const [intelForThreatActorSourceCountries, setIntelForThreatActorSourceCountries] = useState<string>('')
  const [intelForThreatActorTargetCountries, setIntelForThreatActorTargetCountries] = useState<string>('')

  useEffect(() => {
    if (aptData) {
      setIntelForTopThreatActorsIn12Months(mapIntelToTopThreatActorsIn12Months(aptData.topThreatActorsLast12Months))
      setIntelForThreatsActorEventsIn12Months(mapIntelToThreatsActorEventsIn12Months(aptData.threatActorByMonth))

      const { categories, series } = mapIntelToThreatsByActorSource(aptData.threatsByActorSourceCountry)
      setIntelForThreatsByActorSourceCategories(categories)
      setIntelForThreatsByActorSourceSeries(series)

      setIntelForThreatActorSourceCountries(mapIntelToAptSourceMap(aptData.distinctThreatActorsInSource))
      setIntelForThreatActorTargetCountries(mapIntelToAptTargetMap(aptData.distinctThreatActorsTargetingCountryList))
    }
  }, [aptData])

  return (
    <>
      {aptData && (
        <div className="bg-contentBackground h-full max-w-[2000px] mx-auto">
          <div className="pb-9">
            <div className="grid grid-cols-12 gap-4 h-auto mb-4">
              <div className="col-span-12 md:col-span-6">
                <div className="bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4">
                  <div className="flex">
                    <div className="text-mainText text-lg leading-relaxed font-medium">Attack Volume per APT Group</div>
                    <Tooltip
                      content={`See the total number of attacks attributed to each APT group. Higher bars indicate more recorded activity. Hover over a bar to view the exact attack count.`}
                      side="top"
                      align="end"
                      className="max-w-[400px]"
                    >
                      <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                    </Tooltip>
                  </div>
                  <VisualizationsBasicBar
                    chartData={intelForTopThreatActorsIn12Months}
                    axisTitle="Threats"
                    tooltipTitle="Threats"
                  />
                </div>

                <div className="flex-grow h-[900px] bg-cardFill rounded-md border border-mainBorder px-4 py-2">
                  <div className="flex">
                    <div className="text-mainText text-lg leading-relaxed font-medium">
                      APT Group Attacks per Target Country
                    </div>
                    <Tooltip
                      content={`Explore global attack distribution, seeing the number of times each country has been targeted by APT groups. Darker shades indicate higher attack counts. Hover over a country to see the exact number.`}
                      side="top"
                      align="end"
                      className="max-w-[400px]"
                    >
                      <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                    </Tooltip>
                  </div>
                  <VisualizationsBasicMap
                    chartData={intelForThreatActorTargetCountries}
                    mapData={mapData}
                    tooltipTitle="Threat Actors"
                  />
                </div>
              </div>
              <div className="col-span-12 md:col-span-6 bg-cardFill rounded-md border border-mainBorder px-4 py-2">
                <div className="flex">
                  <div className="text-mainText text-lg leading-relaxed font-medium">APT Group Attacks over Time</div>
                  <Tooltip
                    content={`Track attack activity over time for each APT group with this heatmap. Darker shades indicate higher attack volumes. Hover over a cell to see the exact attack count.`}
                    side="top"
                    align="end"
                    className="max-w-[400px]"
                  >
                    <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                  </Tooltip>
                </div>
                <VisualizationsBasicHeatmap
                  chartData={intelForThreatsActorEventsIn12Months}
                  height="1400px"
                  logarithmic
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  APT Groups per Country and Attack Volume
                </div>
                <Tooltip
                  content={`See the global distribution of known APT groups with this map, highlighting the number of groups associated with each country. Darker shades indicate a higher count. Hover over a country to see the exact number.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicMap
                chartData={intelForThreatActorSourceCountries}
                mapData={mapData}
                tooltipTitle="Threat Actors"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Attack Volume per Source Country and APT Group
                </div>
                <Tooltip
                  content={`This stacked bar chart shows the volume of attacks per country, broken down by APT group. Each segment represents the attack count for a specific group. Hover over a bar to see the exact number of attacks by each APT group in that country.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicColumn
                categories={intelForThreatsByActorSourceCategories}
                series={intelForThreatsByActorSourceSeries}
                axisTitle="Threat Actors"
                long
              />
            </div>
          </div>
        </div>
      )}
      {!aptData && <PageSkeleton />}
    </>
  )
}

export default VisualizationAptPage
